import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Tooltip } from '@mui/material'
import React from 'react'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon } from '../../../template/kartera/icons'
import { CUSTOM_PLAN_MAILTO, INCLUDED_MODULES, PLAN_PERIODS, REASONS, usePlanListModel } from './plan-list-model'
import type { PlanCard } from './plan-list-model'

const PlanSkeleton = () => (
    <div className="flex min-h-[360px] flex-col gap-4 rounded-2xl border border-k-line bg-k-surface p-5" aria-hidden="true">
        <span className="h-5 w-1/2 animate-pulse rounded bg-k-line-soft" />
        <span className="h-9 w-2/3 animate-pulse rounded bg-k-line-soft" />
        {[0, 1, 2, 3].map((index) => <span key={index} className="h-4 w-full animate-pulse rounded bg-k-line-soft" />)}
    </div>
)

const PlanArticle = ({ plan, busy, disabled, onSelect }: { plan: PlanCard, busy: boolean, disabled: boolean, onSelect: () => void }) => (
    <article className={`relative flex flex-col gap-4 rounded-2xl bg-k-surface p-5 ${plan.recommended ? 'border-2 border-k-primary shadow-k-pop' : 'border border-k-line'}`}>
        {plan.recommended && (
            <span className="absolute -top-3 left-5 rounded-full bg-k-primary px-2.5 py-0.5 text-xs font-bold !text-white">Recomendado</span>
        )}
        <div className="flex flex-col gap-1 border-b border-k-line-soft pb-4">
            <h3 className="m-0 text-lg font-extrabold text-k-ink">{plan.name}</h3>
            <div className="flex items-baseline gap-1.5">
                <span className="text-[30px] font-extrabold tabular-nums tracking-[-0.03em] text-k-ink">{plan.price}</span>
                <span className="text-sm text-k-muted">MXN {plan.priceSuffix}</span>
            </div>
            {plan.periodLabel && <span className="text-[13px] text-k-muted">{plan.periodLabel}</span>}
        </div>

        <ul className="m-0 flex flex-1 list-none flex-col gap-2.5 p-0">
            {plan.features.map((feature) => (
                <li key={feature.id} className="flex items-center gap-2.5 text-sm">
                    <span className="min-w-0 flex-1 text-k-ink-2">{feature.label}</span>
                    {feature.value && <span className="font-bold tabular-nums text-k-ink">{feature.value}</span>}
                    {feature.help && (
                        <Tooltip title={feature.help}>
                            <HelpOutlineIcon className="text-k-muted-2" style={{ fontSize: 18 }} />
                        </Tooltip>
                    )}
                </li>
            ))}
        </ul>

        <button
            type="button"
            disabled={disabled || !plan.stripeId}
            onClick={onSelect}
            className={`inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl text-sm font-bold disabled:cursor-not-allowed disabled:opacity-40 ${plan.recommended
                ? 'border-0 bg-k-primary !text-white hover:brightness-105'
                : 'border border-k-line bg-k-surface text-k-ink hover:bg-k-hover'}`}
        >
            {busy ? <RefreshIcon className="animate-spin" style={{ fontSize: 18 }} /> : <CheckIcon />}
            {busy ? 'Redirigiendo al pago…' : 'Seleccionar'}
        </button>
    </article>
)

const PlanList = () => {
    const model = usePlanListModel()

    return (
        <KarteraAdminTemplate loading={false} title="Planes">
            <div className="mx-auto flex w-full max-w-[1180px] flex-col gap-[18px]">
                <KarteraPageHeader
                    title="Planes"
                    description="Contrata lo que necesites y cancela cuando quieras, sin plazos forzosos."
                    backTo={model.backTo}
                    backLabel="Mi plan"
                    actions={(
                        <div role="group" aria-label="Periodicidad" className="inline-flex rounded-xl border border-k-line bg-k-surface p-1">
                            {PLAN_PERIODS.map((option) => {
                                const on = model.period === option.id
                                return (
                                    <button
                                        key={option.id}
                                        type="button"
                                        aria-pressed={on}
                                        onClick={() => model.changePeriod(option.id)}
                                        className={`h-9 min-w-[96px] rounded-[9px] border-0 px-4 text-sm font-bold ${on ? 'bg-k-ink text-k-surface' : 'bg-transparent text-k-ink-2 hover:bg-k-hover'}`}
                                    >
                                        {option.label}
                                    </button>
                                )
                            })}
                        </div>
                    )}
                />

                {model.error && (
                    <div className="flex items-center gap-3 rounded-[14px] bg-k-danger-soft px-4 py-3.5 text-sm text-k-danger-ink">
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.error}</p>
                    </div>
                )}

                <div className="grid grid-cols-1 gap-4 pt-2 md:grid-cols-2 xl:grid-cols-4">
                    {model.loading
                        ? [0, 1, 2, 3].map((index) => <PlanSkeleton key={index} />)
                        : model.plans.map((plan) => (
                            <PlanArticle
                                key={plan.id}
                                plan={plan}
                                busy={model.checkoutId === plan.id}
                                disabled={Boolean(model.checkoutId)}
                                onSelect={() => model.selectPlan(plan)}
                            />
                        ))}
                </div>

                {!model.loading && !model.error && model.plans.length === 0 && (
                    <section className="k-card">
                        <p className="m-0 text-sm text-k-muted">No hay planes disponibles para esta periodicidad.</p>
                    </section>
                )}

                <p className="m-0 text-center text-sm text-k-muted">
                    ¿Necesitas un plan personalizado?{' '}
                    <a href={CUSTOM_PLAN_MAILTO} className="font-bold text-k-primary-ink hover:underline">Contáctanos</a>
                </p>

                <section className="k-card">
                    <h2 className="k-card-title">Todos los paquetes incluyen</h2>
                    <ul className="m-0 grid list-none grid-cols-1 gap-x-6 gap-y-2.5 p-0 sm:grid-cols-2 lg:grid-cols-3">
                        {INCLUDED_MODULES.map((label) => (
                            <li key={label} className="flex items-center gap-2.5 text-sm text-k-ink-2">
                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-k-ok-soft text-k-ok">
                                    <CheckIcon size={14} />
                                </span>
                                {label}
                            </li>
                        ))}
                    </ul>
                    <p className="m-0 text-xs text-k-muted">* Todoist es una marca registrada de © Doist Inc.</p>
                </section>

                <section className="k-card">
                    <h2 className="k-card-title">¿Por qué Kartera 365?</h2>
                    <p className="m-0 text-sm text-k-ink-2">Kartera 365 es la mejor plataforma para agentes de seguros.</p>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {REASONS.map((reason) => (
                            <div key={reason.id} className="flex items-start gap-3.5 rounded-[14px] border border-k-line-soft bg-k-sunken p-4">
                                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${reason.tone}`}>
                                    <reason.Icon style={{ fontSize: 22 }} />
                                </span>
                                <div className="flex min-w-0 flex-col gap-1">
                                    <span className="text-sm font-bold text-k-ink">{reason.title}</span>
                                    <span className="text-[13px] leading-[1.5] text-k-muted">{reason.text}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </KarteraAdminTemplate>
    )
}

export default PlanList
